// src/components/ComboboxDatosMaestros.js
import React, { useState, useRef, useEffect } from 'react';
import DatosMaestrosService from '../services/datosMaestrosService';
import { normalizarParaComparar } from '../utils/textoComparable';

const distanciaLevenshtein = (a, b) => {
    const m = a.length, n = b.length;
    if (m === 0) return n;
    if (n === 0) return m;
    const fila = Array.from({ length: n + 1 }, (_, j) => j);
    for (let i = 1; i <= m; i++) {
        let anterior = fila[0];
        fila[0] = i;
        for (let j = 1; j <= n; j++) {
            const temp = fila[j];
            fila[j] = a[i - 1] === b[j - 1]
                ? anterior
                : 1 + Math.min(anterior, fila[j], fila[j - 1]);
            anterior = temp;
        }
    }
    return fila[n];
};

// Detecta variantes tipo "Facebook" / "Facebook-bot" / "Facebook.bot" (una contiene a la otra
// tras normalizar) o simples errores de tipeo (distancia de edición chica en relación al largo).
const sonNombresParecidos = (nombreA, nombreB) => {
    const a = normalizarParaComparar(nombreA);
    const b = normalizarParaComparar(nombreB);
    if (!a || !b || a === b) return false;
    if (a.length >= 4 && b.length >= 4 && (a.includes(b) || b.includes(a))) return true;
    const distancia = distanciaLevenshtein(a, b);
    const ratio = 1 - distancia / Math.max(a.length, b.length);
    return ratio >= 0.75;
};

/**
 * Componente combobox que permite seleccionar de una lista o escribir un valor nuevo
 * Si el valor no existe en la lista, se crea automáticamente al confirmar
 *
 * @param {Array} opciones - Lista de opciones disponibles (debe tener propiedad 'nombre' o 'nombreCompleto')
 * @param {string} valor - Valor actual seleccionado
 * @param {Function} onChange - Callback cuando cambia el valor
 * @param {string} tipo - Tipo de dato maestro ('lugar', 'abogado', 'referencia', etc.)
 * @param {string} tipoLugar - Tipo específico de lugar si es tipo 'lugar' (ej: 'Consultorio', 'Oficina')
 * @param {string} placeholder - Texto placeholder
 * @param {string} label - Etiqueta del campo
 * @param {boolean} required - Si es requerido
 * @param {string} icon - Icono a mostrar (componente de icono)
 * @param {Function} onCrearNuevo - Callback opcional cuando se crea un nuevo elemento
 * @param {Object} datosCreacionExtra - Campos adicionales a mandar al crear (ej. 'ubicacion' para carpeta)
 */
const ComboboxDatosMaestros = ({
                                   opciones = [],
                                   valor,
                                   onChange,
                                   tipo, // 'lugar', 'abogado', 'referencia'
                                   tipoLugar, // Para lugares: 'Consultorio', 'Oficina', 'Sala', etc.
                                   placeholder = "Seleccione o escriba...",
                                   label,
                                   required = false,
                                   icon: Icon,
                                   onCrearNuevo,
                                   datosCreacionExtra = {}
                               }) => {
    const [inputValue, setInputValue] = useState(valor || '');
    const [mostrarSugerencias, setMostrarSugerencias] = useState(false);
    const [sugerencias, setSugerencias] = useState([]);
    const [creando, setCreando] = useState(false);
    const [valorConfirmado, setValorConfirmado] = useState(valor || '');
    // Cuando tipo === 'referencia' y el texto tipeado se parece a una opción existente,
    // se pausa la creación automática y se le pregunta al usuario para evitar duplicados
    // tipo "Facebook" / "Facebook-bot" / "Facebook.bot".
    const [sugerenciaSimilar, setSugerenciaSimilar] = useState(null);
    const wrapperRef = useRef(null);
    const inputRef = useRef(null);
    const seleccionandoRef = useRef(false);

    // Sincronizar inputValue con valor externo solo si cambió externamente
    useEffect(() => {
        if (valor !== valorConfirmado) {
            setInputValue(valor || '');
            setValorConfirmado(valor || '');
        }
    }, [valor]);

    // Cerrar sugerencias al hacer clic fuera
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setMostrarSugerencias(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Obtener el nombre completo de una opción (maneja lugares, abogados, etc.)
    const obtenerNombreCompleto = (opcion) => {
        if (opcion.nombreCompleto) return opcion.nombreCompleto;
        if (opcion.nombre) {
            // Si es abogado, combinar nombre y apellido
            if (opcion.apellido) {
                return `${opcion.nombre} ${opcion.apellido}`;
            }
            return opcion.nombre;
        }
        return '';
    };

    // Filtrar sugerencias basadas en el texto ingresado
    const filtrarSugerencias = (texto) => {
        if (!texto || texto.trim() === '') {
            // Para 'referencia' y 'carpeta' mostramos el catálogo completo al enfocar vacío, para
            // que quede visualmente claro que el campo está conectado a un catálogo
            // (lugar/abogado mantienen el comportamiento previo: nada hasta que se tipea).
            if ((tipo === 'referencia' || tipo === 'carpeta') && opciones.length > 0) {
                setSugerencias(opciones);
                setMostrarSugerencias(true);
            } else {
                setSugerencias([]);
                setMostrarSugerencias(false);
            }
            return;
        }

        const textoLower = texto.toLowerCase().trim();
        const filtradas = opciones.filter(opcion => {
            const nombre = obtenerNombreCompleto(opcion).toLowerCase();
            return nombre.includes(textoLower);
        });

        setSugerencias(filtradas);
        setMostrarSugerencias(filtradas.length > 0 || texto.trim() !== '');
    };

    const handleInputChange = (e) => {
        const nuevoValor = e.target.value;
        setInputValue(nuevoValor);
        filtrarSugerencias(nuevoValor);
        // Mantener sincronizado el formulario padre para evitar perder el valor
        // cuando el usuario confirma rápidamente sin esperar al onBlur.
        onChange(nuevoValor);
    };

    const handleSelectSugerencia = (opcion) => {
        seleccionandoRef.current = true;
        const nombreSeleccionado = obtenerNombreCompleto(opcion);
        setInputValue(nombreSeleccionado);
        setValorConfirmado(nombreSeleccionado);
        setMostrarSugerencias(false);
        // Solo llamar a onChange cuando se selecciona del desplegable
        onChange(nombreSeleccionado);

        // Resetear el flag después de un pequeño delay
        setTimeout(() => {
            seleccionandoRef.current = false;
        }, 300);
    };

    // Verificar si un valor existe en las opciones
    const existeEnOpciones = (nombre) => {
        return opciones.some(opcion => {
            const nombreOpcion = obtenerNombreCompleto(opcion).trim();
            return nombreOpcion.toLowerCase() === nombre.toLowerCase().trim();
        });
    };

    // Crear nuevo elemento si no existe
    const crearNuevoElemento = async (nombre, forzarCreacion = false) => {
        if (!nombre || nombre.trim() === '') return;

        const nombreTrim = nombre.trim();

        // Verificar si ya existe (comparación case-insensitive)
        if (existeEnOpciones(nombreTrim)) {
            // Si ya existe, usar el valor existente
            const opcionExistente = opciones.find(opcion => {
                const nombreOpcion = obtenerNombreCompleto(opcion).trim();
                return nombreOpcion.toLowerCase() === nombreTrim.toLowerCase();
            });

            if (opcionExistente) {
                const nombreCompleto = obtenerNombreCompleto(opcionExistente);
                setInputValue(nombreCompleto);
                setValorConfirmado(nombreCompleto);
                onChange(nombreCompleto);
            }
            return; // Ya existe, no crear
        }

        // Antes de crear una referencia o carpeta nueva, avisar si se parece mucho a una existente
        // (ej. "Facebook-bot" cuando ya existe "Facebook", o "Liquidar 5 B" cuando ya existe
        // "Liquidar 5B") para no seguir fragmentando el catálogo. Para las carpetas es justamente
        // el problema que resolvió pasarlas de texto libre a dato maestro.
        if ((tipo === 'referencia' || tipo === 'carpeta') && !forzarCreacion) {
            const parecida = opciones.find(opcion => sonNombresParecidos(nombreTrim, obtenerNombreCompleto(opcion)));
            if (parecida) {
                setSugerenciaSimilar({ nombreIngresado: nombreTrim, sugerida: parecida });
                return;
            }
        }

        try {
            setCreando(true);
            let nuevoElemento;
            let response;

            if (tipo === 'lugar') {
                nuevoElemento = {
                    nombre: nombreTrim,
                    tipo: tipoLugar || 'Consultorio',
                    direccion: '',
                    telefono: '',
                    descripcion: `Creado automáticamente desde trámite`
                };
                response = await DatosMaestrosService.createLugar(nuevoElemento);
                console.log('✅ Lugar creado automáticamente:', response.data);
            } else if (tipo === 'abogado') {
                nuevoElemento = {
                    nombre: nombreTrim.split(' ')[0] || nombreTrim,
                    apellido: nombreTrim.split(' ').slice(1).join(' ') || '',
                    matricula: '',
                    telefono: '',
                    email: '',
                    descripcion: `Creado automáticamente desde trámite`
                };
                response = await DatosMaestrosService.createAbogado(nuevoElemento);
                console.log('✅ Abogado creado automáticamente:', response.data);
            } else if (tipo === 'referencia') {
                nuevoElemento = {
                    nombre: nombreTrim,
                    descripcion: 'Creado automáticamente',
                    color: '#6c757d' // Color gris por defecto
                };
                response = await DatosMaestrosService.createReferencia(nuevoElemento);
                console.log('✅ Referencia creada automáticamente:', response.data);
            } else if (tipo === 'carpeta') {
                nuevoElemento = {
                    nombre: nombreTrim,
                    ubicacion: (datosCreacionExtra.ubicacion || '').trim(),
                    descripcion: 'Creada desde la ficha del trámite'
                };
                response = await DatosMaestrosService.createCarpeta(nuevoElemento);
                console.log('✅ Carpeta creada automáticamente:', response.data);
            }


            // Actualizar el valor confirmado con el nombre completo del elemento creado
            if (response && response.data) {
                const nombreCompletoCreado = obtenerNombreCompleto(response.data);

                // --- INICIO DEL CAMBIO ---
                // 1. Notificar al padre que se creó un nuevo elemento.
                //    Esto permite al padre actualizar su lista de opciones.
                if (onCrearNuevo) {
                    onCrearNuevo(response.data);
                }

                // 2. Actualizar el estado interno y el valor en el formulario.
                setInputValue(nombreCompletoCreado);
                setValorConfirmado(nombreCompletoCreado);
                onChange(nombreCompletoCreado);
                // --- FIN DEL CAMBIO ---
            }

        } catch (error) {
            console.error('Error al crear nuevo elemento:', error);
            // Si hay error, restaurar el valor anterior
            setInputValue(valorConfirmado);
        } finally {
            setCreando(false);
        }
    };

    // El usuario confirmó que en realidad quería la referencia existente sugerida.
    const usarSugerenciaSimilar = () => {
        const nombreCompleto = obtenerNombreCompleto(sugerenciaSimilar.sugerida);
        setInputValue(nombreCompleto);
        setValorConfirmado(nombreCompleto);
        onChange(nombreCompleto);
        setSugerenciaSimilar(null);
    };

    // El usuario confirmó que quiere crear la referencia nueva de todas formas.
    const crearIgualmente = () => {
        const nombreIngresado = sugerenciaSimilar.nombreIngresado;
        setSugerenciaSimilar(null);
        crearNuevoElemento(nombreIngresado, true);
    };

    // Al perder el foco, verificar si necesita crear o confirmar valor
    const handleBlur = async () => {
        // Pequeño delay para permitir que se ejecute el onClick de las sugerencias
        setTimeout(async () => {
            // Si se está seleccionando del desplegable, no hacer nada
            if (seleccionandoRef.current) {
                return;
            }

            const valorTrim = inputValue.trim();

            // Si el campo está vacío, limpiar
            if (!valorTrim) {
                setInputValue('');
                setValorConfirmado('');
                onChange('');
                return;
            }

            // Si el valor actual ya es el confirmado, no hacer nada
            if (valorTrim === valorConfirmado) {
                return;
            }

            // Si el valor existe en las opciones, confirmarlo
            if (existeEnOpciones(valorTrim)) {
                const opcionExistente = opciones.find(opcion => {
                    const nombreOpcion = obtenerNombreCompleto(opcion).trim();
                    return nombreOpcion.toLowerCase() === valorTrim.toLowerCase();
                });

                if (opcionExistente) {
                    const nombreCompleto = obtenerNombreCompleto(opcionExistente);
                    setInputValue(nombreCompleto);
                    setValorConfirmado(nombreCompleto);
                    onChange(nombreCompleto);
                }
            } else {
                // Si no existe, crear nuevo elemento solo si es un valor completo (más de 2 caracteres)
                // Esto evita crear elementos con valores parciales como "Eu"
                if (valorTrim.length > 2) {
                    await crearNuevoElemento(valorTrim);
                } else {
                    // Si es muy corto, restaurar el valor anterior o limpiar
                    if (valorConfirmado) {
                        setInputValue(valorConfirmado);
                    } else {
                        setInputValue('');
                        onChange('');
                    }
                }
            }
        }, 250);
    };

    const handleFocus = () => {
        filtrarSugerencias(inputValue);
    };

    return (
        <div className="position-relative" ref={wrapperRef}>
            {label && (
                <label className="form-label fw-semibold">
                    {label} {required && <span className="text-danger">*</span>}
                </label>
            )}
            <div className="input-group position-relative">
                {Icon && (
                    <span className="input-group-text bg-light">
                        <Icon fontSize="small" />
                    </span>
                )}
                <input
                    ref={inputRef}
                    type="text"
                    className="form-control shadow-sm"
                    value={inputValue}
                    onChange={handleInputChange}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    placeholder={placeholder}
                    required={required}
                    style={{
                        cursor: 'text',
                        paddingRight: '2.5rem'
                    }}
                />
                <span
                    className="position-absolute"
                    style={{
                        right: '0.75rem',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        pointerEvents: 'none',
                        color: '#6c757d'
                    }}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                        <path fillRule="evenodd" d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"/>
                    </svg>
                </span>
                {creando && (
                    <span className="input-group-text bg-light">
                        <span className="spinner-border spinner-border-sm text-primary" role="status">
                            <span className="visually-hidden">Creando...</span>
                        </span>
                    </span>
                )}
            </div>

            {/* Aviso de referencia parecida a una existente (evita duplicados tipo Facebook/Facebook-bot) */}
            {sugerenciaSimilar && (
                <div className="alert alert-warning py-2 px-3 mt-2 mb-0" style={{ fontSize: '0.85rem' }}>
                    <div className="mb-2">
                        <i className="bi bi-exclamation-triangle-fill me-1"></i>
                        ¿Quisiste decir "<strong>{obtenerNombreCompleto(sugerenciaSimilar.sugerida)}</strong>"?
                    </div>
                    <div className="d-flex gap-2">
                        <button type="button" className="btn btn-sm btn-primary" onClick={usarSugerenciaSimilar}>
                            Usar esa
                        </button>
                        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={crearIgualmente}>
                            No, crear "{sugerenciaSimilar.nombreIngresado}"
                        </button>
                    </div>
                </div>
            )}

            {/* Lista de sugerencias */}
            {mostrarSugerencias && sugerencias.length > 0 && (
                <div
                    className="list-group position-absolute w-100 shadow-sm"
                    style={{
                        zIndex: 9999,
                        maxHeight: '200px',
                        overflowY: 'auto',
                        marginTop: '2px',
                        borderRadius: '0.375rem'
                    }}
                >
                    {sugerencias.map((opcion, index) => {
                        const nombre = obtenerNombreCompleto(opcion);
                        const direccion = opcion.direccion || '';
                        const matricula = opcion.matricula || '';
                        const color = opcion.color;

                        return (
                            <button
                                key={opcion.id || index}
                                type="button"
                                className="list-group-item list-group-item-action d-flex align-items-center"
                                onClick={() => handleSelectSugerencia(opcion)}
                                style={{
                                    cursor: 'pointer',
                                    border: '1px solid #dee2e6',
                                    fontSize: '0.9rem'
                                }}
                            >
                                {color && (
                                    <span
                                        className="d-inline-block me-2"
                                        style={{
                                            width: '1rem',
                                            height: '1rem',
                                            backgroundColor: color,
                                            borderRadius: '3px',
                                            flexShrink: 0
                                        }}
                                    ></span>
                                )}
                                <div>
                                    <strong>{nombre}</strong>
                                    {direccion && <span className="text-muted ms-2">- {direccion}</span>}
                                    {matricula && <span className="text-muted ms-2">(Mat: {matricula})</span>}
                                </div>
                            </button>
                        );
                    })}
                </div>
            )}

            {/* Indicador de que se puede escribir nuevo valor */}
            {inputValue && inputValue.trim() !== '' && !existeEnOpciones(inputValue) && (
                <small className="text-muted d-block mt-1">
                    <i className="bi bi-plus-circle me-1"></i>
                    Se creará automáticamente al confirmar
                </small>
            )}
        </div>
    );
};

export default ComboboxDatosMaestros;
